/* eslint-disable */
import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ShoppingBag,
  CheckCircle,
  XCircle,
  ShoppingCart,
  ArrowUp,
  ArrowDown,
  CreditCard,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Timestamp } from "firebase/firestore";
import workerAPI from "../../../../api/worker/worker_service";

export default function DashboardCardsDigital() {
  const account = useSelector((state) => state.AccountReducer);
  const [orders, setOrders] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (!account) return;

    const fetchOrders = async () => {
      try {
        const now = new Date();
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);

        const startTimestamp = Timestamp.fromDate(startOfYesterday);
        const endTimestamp = Timestamp.fromDate(now);

        const fetchedOrders = await workerAPI.getOrders(
          startTimestamp,
          endTimestamp
        );

        const list = fetchedOrders
          .filter((order) => order.type === "digitalProducts")
          .filter((order) => order.deleted !== 1);

        setOrders(list);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, [account]);

  const getTimestamps = () => {
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const endOfYesterday = new Date(startOfToday);

    return { startOfToday, endOfToday, startOfYesterday, endOfYesterday };
  };

  const { startOfToday, endOfToday, startOfYesterday, endOfYesterday } =
    getTimestamps();

  const getOrderTimeMs = (order) => {
    if (order?.timeStamp?._seconds) return order.timeStamp._seconds * 1000;
    if (order?.timeStamp?.seconds) return order.timeStamp.seconds * 1000;
    if (typeof order?.timeStamp === "number") return order.timeStamp;
    return null;
  };

  const todayDigitalOrders = orders.filter((order) => {
    const orderTimeMs = getOrderTimeMs(order);
    if (!orderTimeMs) return false;
    const orderTime = new Date(orderTimeMs);
    return orderTime >= startOfToday && orderTime <= endOfToday;
  });

  const yesterdayDigitalOrders = orders.filter((order) => {
    const orderTimeMs = getOrderTimeMs(order);
    if (!orderTimeMs) return false;
    const orderTime = new Date(orderTimeMs);
    return orderTime >= startOfYesterday && orderTime <= endOfYesterday;
  });

  const calculateTrend = (today, yesterday) => {
    if (yesterday === 0) return "N/A";
    const trend = ((today - yesterday) / yesterday) * 100;
    return `${trend.toFixed(1)}%`;
  };

  const data = useMemo(
    () => [
      {
        title: t("dashboard.digitalcards.statuses.waitingPayment"),
        value:
          todayDigitalOrders.filter((order) => order.status == 1).length || 0,
        yesterdayValue:
          yesterdayDigitalOrders.filter((order) => order.status == 1).length ||
          0,
        icon: <CreditCard size={20} />,
        link: "/orders/digital?today=1&status=1",
        color: "purple",
      },
      {
        title: t("dashboard.digitalcards.statuses.waitingConfirmation"),
        value:
          todayDigitalOrders.filter((order) => order.status == 2).length || 0,
        yesterdayValue:
          yesterdayDigitalOrders.filter((order) => order.status == 2).length ||
          0,
        icon: <ShoppingBag size={20} />,
        link: "/orders/digital?today=1&status=2",
        color: "teal",
      },
      {
        title: t("dashboard.digitalcards.statuses.paid"),
        value:
          todayDigitalOrders.filter((order) => order.status == 3).length || 0,
        yesterdayValue:
          yesterdayDigitalOrders.filter((order) => order.status == 3).length ||
          0,
        icon: <CheckCircle size={20} />,
        link: "/orders/digital?today=1&status=3",
        color: "rose",
      },
      {
        title: t("dashboard.digitalcards.statuses.paidAndDelivered"),
        value:
          todayDigitalOrders.filter((order) => order.status === 5).length || 0,
        yesterdayValue:
          yesterdayDigitalOrders.filter((order) => order.status == 5).length ||
          0,
        icon: <ShoppingCart size={20} />,
        link: "/orders/digital?today=1&status=5",
        color: "amber",
      },
    ],
    [todayDigitalOrders, yesterdayDigitalOrders]
  );

  const colorClasses = {
    purple: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
    teal: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
    rose: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
    amber: {
      bg: "bg-purple-500",
      text: "text-[#9e68ba]",
      light: "bg-[#fcf6ff]",
      border: "border-[#fbf4ff]",
    },
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
      {data.map((item, index) => {
        const trend = calculateTrend(item.value, item.yesterdayValue);
        const trendUp = item.value >= item.yesterdayValue;
        const trendColor = trendUp ? "text-green-500" : "text-red-500";
        const colorClass = colorClasses[item.color];

        return (
          <Link
            to={item.link}
            key={index}
            className={`block bg-white dark:!bg-[#181919] rounded-lg border ${colorClass.border} dark:!border-[#3c3a3d] overflow-hidden group`}
            dir="rtl"
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-[#3c3a3d]">
              <h3 className="font-medium text-xl text-gray-700 dark:text-white">{item.title}</h3>
              <div
                className={`p-2 rounded-full ${colorClass.light} ${colorClass.text}`}
              >
                {item.icon}
              </div>
            </div>

            {/* Main content with large number and trend */}
            <div className="p-4">
              <div className="flex items-end justify-between">
                <div className="text-3xl font-bold dark:text-white">{item.value}</div>

                {trend !== "N/A" && (
                  <div className={`flex items-center ${trendColor} text-sm`}>
                    {trendUp ? (
                      <ArrowUp size={14} className="ml-1" />
                    ) : (
                      <ArrowDown size={14} className="ml-1" />
                    )}
                    {trend}
                  </div>
                )}
              </div>

              {/* Yesterday comparison */}
              <div className="mt-2 text-xs text-gray-500 dark:text-white">
                {t("dashboard.digitalcards.yesterday")}: {item.yesterdayValue}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
